import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Bar, BarChart, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts';
import {get, ref} from 'firebase/database';
import {database} from '../../config/firebase';
import {useAuth} from '../../hooks/useAuth';
import {useStatistics} from '../../hooks/useStatistics';
import {ExportService} from '../../services/exportService';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

const DesktopDashboard = () => {
    const {userRole} = useAuth();
    const [selectedChurchId, setSelectedChurchId] = useState(null);
    const [churches, setChurches] = useState([]);
    const {
        statistics,
        loading,
        error,
        getGenderDistributionData,
        getBaptizedDistributionData,
        getProfessionsData
    } = useStatistics(selectedChurchId);
    const navigate = useNavigate();

    // Récupérer la liste des églises pour l'admin
    useEffect(() => {
        const fetchChurches = async () => {
            if (userRole?.role === 'admin') {
                try {
                    const churchesRef = ref(database, 'churches');
                    const snapshot = await get(churchesRef);

                    if (snapshot.exists()) {
                        const churchesData = [];
                        snapshot.forEach((childSnapshot) => {
                            churchesData.push({
                                id: childSnapshot.key,
                                ...childSnapshot.val()
                            });
                        });
                        setChurches(churchesData);
                    }
                } catch (error) {
                    console.error("Erreur lors de la récupération des églises:", error);
                }
            } else if (userRole?.role === 'church_admin') {
                // Pour un admin d'église, définir par défaut son église
                setSelectedChurchId(userRole.churchId);
            }
        };

        fetchChurches();
    }, [userRole]);

    const handleExportStats = async () => {
        const result = await ExportService.exportStatsToPDF(statistics, selectedChurchId);
        if (!result.success) {
            alert(`Erreur lors de l'exportation: ${result.error}`);
        }
    };

    const handleExportMembers = async () => {
        const result = await ExportService.exportToPDF(selectedChurchId);
        if (!result.success) {
            alert(`Erreur lors de l'exportation: ${result.error}`);
        }
    };

    const handleChurchChange = (event) => {
        setSelectedChurchId(event.target.value === 'all' ? null : event.target.value);
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
                <p>{error}</p>
            </div>
        );
    }

    const genderData = getGenderDistributionData();
    const baptizedData = getBaptizedDistributionData();
    const professionsData = getProfessionsData().slice(0, 6); // Top 6 professions

    // Calculer les totaux
    const totalMembers = statistics.global?.totalMembres || 0;
    const currentChurchMembers = selectedChurchId && statistics.church ?
        (typeof statistics.church === 'object' && !Array.isArray(statistics.church) ?
            statistics.church.totalMembres :
            statistics.church[selectedChurchId]?.totalMembres) || 0 : 0;

    return (
        <div className="mx-auto px-4 py-6">
            <div className="mb-6 flex justify-between items-center">
                <h1 className="text-2xl font-bold">Tableau de bord</h1>

                <div className="flex gap-4">
                    {userRole?.role === 'admin' && (
                        <select
                            value={selectedChurchId || 'all'}
                            onChange={handleChurchChange}
                            className="px-4 py-2 border border-gray-300 rounded-md"
                        >
                            <option value="all">Toutes les églises</option>
                            {churches.map(church => (
                                <option key={church.id} value={church.id}>
                                    {church.nom}
                                </option>
                            ))}
                        </select>
                    )}

                    <button
                        onClick={handleExportStats}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                        Exporter statistiques
                    </button>

                    <button
                        onClick={handleExportMembers}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                    >
                        Exporter membres
                    </button>
                </div>
            </div>

            {/* Cards de statistiques */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h2 className="text-xl font-semibold mb-2">Total des membres</h2>
                    <p className="text-3xl font-bold text-blue-600">
                        {selectedChurchId ? currentChurchMembers : totalMembers}
                    </p>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h2 className="text-xl font-semibold mb-2">Hommes</h2>
                    <p className="text-3xl font-bold text-green-600">
                        {selectedChurchId && statistics.church ?
                            (typeof statistics.church === 'object' && !Array.isArray(statistics.church) ?
                                statistics.church.hommes :
                                statistics.church[selectedChurchId]?.hommes) || 0 :
                            statistics.global?.hommes || 0}
                    </p>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h2 className="text-xl font-semibold mb-2">Femmes</h2>
                    <p className="text-3xl font-bold text-purple-600">
                        {selectedChurchId && statistics.church ?
                            (typeof statistics.church === 'object' && !Array.isArray(statistics.church) ?
                                statistics.church.femmes :
                                statistics.church[selectedChurchId]?.femmes) || 0 :
                            statistics.global?.femmes || 0}
                    </p>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h2 className="text-xl font-semibold mb-2">Baptisés</h2>
                    <p className="text-3xl font-bold text-amber-600">
                        {selectedChurchId && statistics.church ?
                            (typeof statistics.church === 'object' && !Array.isArray(statistics.church) ?
                                statistics.church.baptises :
                                statistics.church[selectedChurchId]?.baptises) || 0 :
                            statistics.global?.baptises || 0}
                    </p>
                </div>
            </div>

            {/* Graphiques */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {/* Répartition par sexe */}
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h2 className="text-xl font-semibold mb-4">Répartition par sexe</h2>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={genderData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                                label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            >
                                {genderData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]}/>
                                ))}
                            </Pie>
                            <Tooltip formatter={(value) => `${value} membres`}/>
                            <Legend/>
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                {/* Répartition baptisés/non-baptisés */} {/* Répartition baptisés/non-baptisés */}
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h2 className="text-xl font-semibold mb-4">Baptisés / Non-baptisés</h2>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={baptizedData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                                label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            >
                                {baptizedData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]}/>
                                ))}
                            </Pie>
                            <Tooltip formatter={(value) => `${value} membres`}/>
                            <Legend/>
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                {/* Répartition par profession */}
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h2 className="text-xl font-semibold mb-4">Principales professions</h2>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={professionsData}>
                            <XAxis dataKey="name"/>
                            <YAxis/>
                            <Tooltip/>
                            <Bar dataKey="value" name="Membres" fill="#8884d8">
                                {professionsData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]}/>
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Actions rapides */}
            <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                <h2 className="text-xl font-semibold mb-4">Actions rapides</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button
                        onClick={() => navigate('/members')}
                        className="px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center justify-center"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20"
                             fill="currentColor">
                            <path
                                d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v1h8v-1zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-1a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v1h-3zM4.75 14.094A5.973 5.973 0 004 17v1H1v-1a3 3 0 013.75-2.906z"/>
                        </svg>
                        Gérer les membres
                    </button>

                    <button
                        onClick={() => navigate('/churches/summary')}
                        className="px-4 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center justify-center"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20"
                             fill="currentColor">
                            <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z"/>
                            <path
                                d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z"/>
                        </svg>
                        Récapitulatif des églises
                    </button>

                    {userRole?.role === 'admin' && (
                        <button
                            onClick={() => navigate('/churches')}
                            className="px-4 py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center justify-center"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20"
                                 fill="currentColor">
                                <path
                                    d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"/>
                            </svg>
                            Gérer les églises
                        </button>
                    )}
                </div>
            </div>

            {/* Information sur la dernière mise à jour */}
            <div className="text-right text-sm text-white">
                Dernière mise à jour: {new Date().toLocaleString()}
            </div>
        </div>
    );
};

export default DesktopDashboard;
